import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import { AnimatedSection } from "@/components/AnimatedSection";
import { Loader2 } from "lucide-react";

const commercialFormSchema = z.object({
  name: z.string().min(2, "Nombre muy corto").max(100, "Nombre muy largo"),
  phone: z.string().min(8, "Teléfono inválido").max(20, "Teléfono muy largo"),
  email: z.string().email("Email inválido").max(255, "Email muy largo"),
  businessName: z.string().min(2, "Nombre del comercio muy corto").max(150, "Nombre muy largo"),
  address: z.string().min(10, "Dirección incompleta").max(200, "Dirección muy larga"),
  comuna: z.string().min(1, "Selecciona una comuna"),
  contactPerson: z.string().max(100).optional(),
  localSize: z.string().optional(),
  equipmentCount: z.string().max(200).optional(),
  businessHours: z.string().max(100).optional(),
  availableSchedule: z.array(z.string()).optional(),
  comments: z.string().max(500).optional(),
  captcha: z.string().min(1, "Resuelve el CAPTCHA"),
});

type CommercialFormValues = z.infer<typeof commercialFormSchema>;

const CommercialForm = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [num1] = useState(Math.floor(Math.random() * 10) + 1);
  const [num2] = useState(Math.floor(Math.random() * 10) + 1);
  const { toast } = useToast();

  const comunasRM = [
    "Santiago", "Cerrillos", "Cerro Navia", "Conchalí", "El Bosque", "Estación Central",
    "Huechuraba", "Independencia", "La Cisterna", "La Florida", "La Granja", "La Pintana",
    "La Reina", "Las Condes", "Lo Barnechea", "Lo Espejo", "Lo Prado", "Macul",
    "Maipú", "Ñuñoa", "Pedro Aguirre Cerda", "Peñalolén", "Providencia", "Pudahuel",
    "Quilicura", "Quinta Normal", "Recoleta", "Renca", "San Joaquín", "San Miguel",
    "San Ramón", "Vitacura", "Puente Alto", "Pirque", "San José de Maipo",
    "Colina", "Lampa", "Tiltil", "San Bernardo", "Buin", "Calera de Tango",
    "Paine", "Melipilla", "Alhué", "Curacaví", "María Pinto", "San Pedro",
    "Talagante", "El Monte", "Isla de Maipo", "Padre Hurtado", "Peñaflor"
  ];

  const scheduleOptions = [
    "Lunes a Viernes (mañana 8-13hrs)",
    "Lunes a Viernes (tarde 14-19hrs)",
    "Sábados",
    "Domingos",
    "Fuera de horario (noche/madrugada)"
  ];

  const form = useForm<CommercialFormValues>({
    resolver: zodResolver(commercialFormSchema),
    defaultValues: {
      name: "",
      phone: "",
      email: "",
      businessName: "",
      address: "",
      comuna: "",
      contactPerson: "",
      localSize: "",
      equipmentCount: "",
      businessHours: "",
      availableSchedule: [],
      comments: "",
      captcha: "",
    },
  });

  const onSubmit = async (data: CommercialFormValues) => {
    const expectedAnswer = (num1 + num2).toString();
    if (data.captcha !== expectedAnswer) {
      toast({
        variant: "destructive",
        title: "Error en CAPTCHA",
        description: "La respuesta matemática es incorrecta. Por favor, intenta nuevamente.",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch("/send-email.php", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          ...data,
          formType: "commercial",
        }),
      });

      const result = await response.json();

      if (response.ok && result.success) {
        toast({
          title: "¡Solicitud Enviada!",
          description: "Te contactaremos en las próximas 24 horas para coordinar la visita.",
        });
        form.reset();
      } else {
        throw new Error(result.message || "Error al enviar el formulario");
      }
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "No se pudo enviar la solicitud. Por favor, intenta nuevamente o llámanos directamente.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="formulario" className="relative py-24 md:py-32 overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-dark-section via-industrial-gray/95 to-dark-section">
        <div className="absolute top-20 right-10 w-96 h-96 bg-primary/30 rounded-full blur-3xl animate-liquid-blob opacity-20" />
        <div className="absolute bottom-20 left-10 w-96 h-96 bg-accent/20 rounded-full blur-3xl animate-liquid-blob-delayed opacity-20" />
      </div>

      <div className="relative z-10 container mx-auto px-4">
        <AnimatedSection animation="fade-in">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-montserrat font-bold text-white text-center mb-12">
            Solicita tu Revisión GRATIS
          </h2>
          <p className="text-lg text-white/70 text-center mb-12">
            Sin compromiso • Respuesta en 24 horas
          </p>
        </AnimatedSection>

        <AnimatedSection animation="slide-up" delay={100}>
          <div className="max-w-4xl mx-auto backdrop-blur-2xl bg-white/10 border-2 border-white/20 rounded-3xl p-8 md:p-12 shadow-liquid-2xl">
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
                {/* Contact Information */}
                <div className="space-y-4">
                  <h3 className="text-xl font-montserrat font-bold text-white border-b border-white/20 pb-2">
                    Información de Contacto *
                  </h3>
                  
                  <div className="grid md:grid-cols-2 gap-6">
                    <FormField
                      control={form.control}
                      name="name"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-white mb-2">Nombre Completo *</FormLabel>
                          <FormControl>
                            <Input
                              {...field}
                              placeholder="Tu nombre"
                              className="bg-white/10 border-white/20 text-white placeholder:text-white/50"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="phone"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-white mb-2">Teléfono *</FormLabel>
                          <FormControl>
                            <Input
                              {...field}
                              placeholder="+56 9 1234 5678"
                              className="bg-white/10 border-white/20 text-white placeholder:text-white/50"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-white mb-2">Email *</FormLabel>
                        <FormControl>
                          <Input
                            {...field}
                            type="email"
                            placeholder="tu@email.com"
                            className="bg-white/10 border-white/20 text-white placeholder:text-white/50"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                {/* Business Information */}
                <div className="space-y-4">
                  <h3 className="text-xl font-montserrat font-bold text-white border-b border-white/20 pb-2">
                    Información del Negocio *
                  </h3>

                  <FormField
                    control={form.control}
                    name="businessName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-white mb-2">Nombre del Comercio/Local *</FormLabel>
                        <FormControl>
                          <Input
                            {...field}
                            placeholder="Ej: Minimarket Don José"
                            className="bg-white/10 border-white/20 text-white placeholder:text-white/50"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div className="grid md:grid-cols-2 gap-6">
                    <FormField
                      control={form.control}
                      name="address"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-white mb-2">Dirección Completa *</FormLabel>
                          <FormControl>
                            <Input
                              {...field}
                              placeholder="Calle, número"
                              className="bg-white/10 border-white/20 text-white placeholder:text-white/50"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="comuna"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-white mb-2">Comuna *</FormLabel>
                          <Select onValueChange={field.onChange} value={field.value}>
                            <FormControl>
                              <SelectTrigger className="bg-white/10 border-white/20 text-white">
                                <SelectValue placeholder="Selecciona comuna" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent className="max-h-60 z-50">
                              {comunasRM.map((comuna) => (
                                <SelectItem key={comuna} value={comuna}>
                                  {comuna}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </div>

                {/* Technical Information (Optional) */}
                <div className="space-y-4">
                  <h3 className="text-xl font-montserrat font-bold text-white border-b border-white/20 pb-2">
                    Información Técnica (Opcional)
                  </h3>

                  <div className="grid md:grid-cols-2 gap-6">
                    <FormField
                      control={form.control}
                      name="contactPerson"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-white mb-2">Encargado/Contacto en Local</FormLabel>
                          <FormControl>
                            <Input
                              {...field}
                              placeholder="Nombre del encargado"
                              className="bg-white/10 border-white/20 text-white placeholder:text-white/50"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="localSize"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-white mb-2">Tamaño Aproximado del Local</FormLabel>
                          <Select onValueChange={field.onChange} value={field.value}>
                            <FormControl>
                              <SelectTrigger className="bg-white/10 border-white/20 text-white">
                                <SelectValue placeholder="Selecciona tamaño" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="small">Menos de 30m²</SelectItem>
                              <SelectItem value="medium">30-60m²</SelectItem>
                              <SelectItem value="large">60-100m²</SelectItem>
                              <SelectItem value="xlarge">Más de 100m²</SelectItem>
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <FormField
                    control={form.control}
                    name="equipmentCount"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-white mb-2">Cantidad de Equipos Eléctricos Conectados</FormLabel>
                        <FormControl>
                          <Input
                            {...field}
                            placeholder="Ej: 3 refrigeradores, 2 computadores, 1 caja registradora"
                            className="bg-white/10 border-white/20 text-white placeholder:text-white/50"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="businessHours"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-white mb-2">Horarios de Atención del Local</FormLabel>
                        <FormControl>
                          <Input
                            {...field}
                            placeholder="Ej: Lun-Vie 9:00-20:00, Sáb 10:00-14:00"
                            className="bg-white/10 border-white/20 text-white placeholder:text-white/50"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="availableSchedule"
                    render={() => (
                      <FormItem>
                        <FormLabel className="text-white mb-2">Horarios Disponibles para Mantención</FormLabel>
                        <div className="space-y-4">
                          {scheduleOptions.map((option) => (
                            <FormField
                              key={option}
                              control={form.control}
                              name="availableSchedule"
                              render={({ field }) => (
                                <FormItem className="flex items-center space-x-3 space-y-0">
                                  <FormControl>
                                    <Checkbox
                                      checked={field.value?.includes(option)}
                                      onCheckedChange={(checked) => {
                                        const current = field.value || [];
                                        return checked
                                          ? field.onChange([...current, option])
                                          : field.onChange(current.filter((value) => value !== option));
                                      }}
                                      className="border-white/20"
                                    />
                                  </FormControl>
                                  <FormLabel className="text-white/80 font-normal cursor-pointer">
                                    {option}
                                  </FormLabel>
                                </FormItem>
                              )}
                            />
                          ))}
                        </div>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="comments"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-white mb-2">Comentarios Adicionales</FormLabel>
                        <FormControl>
                          <Textarea
                            {...field}
                            placeholder="Cuéntanos si tienes algún problema específico o requerimiento especial"
                            className="bg-white/10 border-white/20 text-white placeholder:text-white/50 min-h-24"
                            maxLength={500}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                {/* CAPTCHA */}
                <FormField
                  control={form.control}
                  name="captcha"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-white mb-2">
                        Verificación: ¿Cuánto es {num1} + {num2}? *
                      </FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          type="number"
                          placeholder="Tu respuesta"
                          className="bg-white/10 border-white/20 text-white placeholder:text-white/50"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Legal Text */}
                <p className="text-xs text-white/50 leading-relaxed">
                  Al enviar este formulario, aceptas que MYCING te contacte para coordinar la visita técnica.
                  Tus datos están protegidos según la Ley 19.628.
                </p>

                {/* Submit Button */}
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full glass-liquid-medium bg-primary/90 hover:bg-primary text-white text-lg py-6 h-auto shadow-liquid-xl hover:shadow-liquid-2xl"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                      Enviando...
                    </>
                  ) : (
                    "Solicitar Revisión GRATIS →"
                  )}
                </Button>
              </form>
            </Form>
          </div>
        </AnimatedSection>
      </div>
    </section>
  );
};

export default CommercialForm;
